export interface TunableConfig {
  player: {
    speedMultiplier: number;
    maxHpBonus: number;
  };
  partners: {
    damageMultiplier: number;
    rangeMultiplier: number;
    attackSpeedMultiplier: number;
    projectileSpeedMultiplier: number;
  };
  enemies: {
    hpMultiplier: number;
    hpPerWave: number;
    speedMultiplier: number;
    speedPerWave: number;
    collisionDamageMultiplier: number;
    maxActiveBase: number;
    maxActivePerWave: number;
    spawnIntervalBaseMs: number;
    spawnIntervalStepMs: number;
    minSpawnIntervalMs: number;
    spawnIntervalMultiplier: number;
    specialUnlockTimeMultiplier: number;
  };
  economy: {
    materialValue: number;
    materialDropsPerEnemy: number;
    pickupRadiusBaseMultiplier: number;
    shopPriceMultiplier: number;
    rerollCostMultiplier: number;
  };
  pricing: {
    partnerPriceMultiplier: number;
    relicPriceMultiplier: number;
    partnerLevel2PriceMultiplier: number;
    partnerLevel3PriceMultiplier: number;
    partnerLevel4PriceMultiplier: number;
    partnerLevel5PriceMultiplier: number;
    rerollBaseCost: number;
    rerollCostPerTwoWaves: number;
    rerollSecondCostMultiplier: number;
    rerollThirdCostMultiplier: number;
    rerollMaxCostMultiplier: number;
    sellRefundRate: number;
    firstWaveMinPartnerBudget: number;
  };
  waves: {
    firstDurationSeconds: number;
    durationStepSeconds: number;
    maxDurationSeconds: number;
    levelUpHeal: number;
  };
  boss: {
    hp: number;
    radius: number;
    phaseOneSpeed: number;
    phaseTwoSpeed: number;
    collisionDamage: number;
    shockwaveDamage: number;
    minionHp: number;
    minionSpeed: number;
    minionCollisionDamage: number;
  };
  bossLab: {
    hpMultiplier: number;
    speedMultiplier: number;
    damageMultiplier: number;
  };
  specialDrops: {
    upgradeDropChance: number;
    treasureChancePerWave: number;
    treasureMinWave: number;
    treasureSpawnDelayMs: number;
    eliteChancePerWave: number;
    eliteMinWave: number;
    eliteHpMultiplier: number;
    eliteSpeedMultiplier: number;
    eliteMaterialReward: number;
    healAmount: number;
    xpAmount: number;
    hasteDurationMs: number;
    hasteMoveSpeedBonus: number;
  };
}

export const DEFAULT_TUNABLES: TunableConfig = {
  player: {
    speedMultiplier: 1,
    maxHpBonus: 0,
  },
  partners: {
    damageMultiplier: 1.12,
    rangeMultiplier: 1.1,
    attackSpeedMultiplier: 1.08,
    projectileSpeedMultiplier: 1.05,
  },
  enemies: {
    hpMultiplier: 0.5,
    hpPerWave: 0.08,
    speedMultiplier: 0.82,
    speedPerWave: 0.04,
    collisionDamageMultiplier: 0.7,
    maxActiveBase: 8,
    maxActivePerWave: 2,
    spawnIntervalBaseMs: 1250,
    spawnIntervalStepMs: 80,
    minSpawnIntervalMs: 420,
    spawnIntervalMultiplier: 1,
    specialUnlockTimeMultiplier: 1.35,
  },
  economy: {
    materialValue: 3,
    materialDropsPerEnemy: 1.4,
    pickupRadiusBaseMultiplier: 3.5,
    shopPriceMultiplier: 0.72,
    rerollCostMultiplier: 0.55,
  },
  pricing: {
    partnerPriceMultiplier: 1,
    relicPriceMultiplier: 1,
    partnerLevel2PriceMultiplier: 1.85,
    partnerLevel3PriceMultiplier: 3.4,
    partnerLevel4PriceMultiplier: 6.5,
    partnerLevel5PriceMultiplier: 12,
    rerollBaseCost: 2,
    rerollCostPerTwoWaves: 1,
    rerollSecondCostMultiplier: 1.5,
    rerollThirdCostMultiplier: 2,
    rerollMaxCostMultiplier: 3,
    sellRefundRate: 0.5,
    firstWaveMinPartnerBudget: 45,
  },
  waves: {
    firstDurationSeconds: 45,
    durationStepSeconds: 10,
    maxDurationSeconds: 90,
    levelUpHeal: 10,
  },
  boss: {
    hp: 260,
    radius: 30,
    phaseOneSpeed: 112,
    phaseTwoSpeed: 168,
    collisionDamage: 24,
    shockwaveDamage: 18,
    minionHp: 8,
    minionSpeed: 122,
    minionCollisionDamage: 10,
  },
  bossLab: {
    hpMultiplier: 1,
    speedMultiplier: 1,
    damageMultiplier: 1,
  },
  specialDrops: {
    upgradeDropChance: 0.85,
    treasureChancePerWave: 1,
    treasureMinWave: 1,
    treasureSpawnDelayMs: 10000,
    eliteChancePerWave: 0.8,
    eliteMinWave: 1,
    eliteHpMultiplier: 2,
    eliteSpeedMultiplier: 0.8,
    eliteMaterialReward: 12,
    healAmount: 10,
    xpAmount: 10,
    hasteDurationMs: 5000,
    hasteMoveSpeedBonus: 0.2,
  },
};

type TunablePath =
  | 'player.speedMultiplier'
  | 'player.maxHpBonus'
  | 'partners.damageMultiplier'
  | 'partners.rangeMultiplier'
  | 'partners.attackSpeedMultiplier'
  | 'partners.projectileSpeedMultiplier'
  | 'enemies.hpMultiplier'
  | 'enemies.hpPerWave'
  | 'enemies.speedMultiplier'
  | 'enemies.speedPerWave'
  | 'enemies.collisionDamageMultiplier'
  | 'enemies.maxActiveBase'
  | 'enemies.maxActivePerWave'
  | 'enemies.spawnIntervalBaseMs'
  | 'enemies.spawnIntervalStepMs'
  | 'enemies.minSpawnIntervalMs'
  | 'enemies.spawnIntervalMultiplier'
  | 'enemies.specialUnlockTimeMultiplier'
  | 'economy.materialValue'
  | 'economy.materialDropsPerEnemy'
  | 'economy.pickupRadiusBaseMultiplier'
  | 'economy.shopPriceMultiplier'
  | 'economy.rerollCostMultiplier'
  | 'pricing.partnerPriceMultiplier'
  | 'pricing.relicPriceMultiplier'
  | 'pricing.partnerLevel2PriceMultiplier'
  | 'pricing.partnerLevel3PriceMultiplier'
  | 'pricing.partnerLevel4PriceMultiplier'
  | 'pricing.partnerLevel5PriceMultiplier'
  | 'pricing.rerollBaseCost'
  | 'pricing.rerollCostPerTwoWaves'
  | 'pricing.rerollSecondCostMultiplier'
  | 'pricing.rerollThirdCostMultiplier'
  | 'pricing.rerollMaxCostMultiplier'
  | 'pricing.sellRefundRate'
  | 'pricing.firstWaveMinPartnerBudget'
  | 'waves.firstDurationSeconds'
  | 'waves.durationStepSeconds'
  | 'waves.maxDurationSeconds'
  | 'waves.levelUpHeal'
  | 'boss.hp'
  | 'boss.radius'
  | 'boss.phaseOneSpeed'
  | 'boss.phaseTwoSpeed'
  | 'boss.collisionDamage'
  | 'boss.shockwaveDamage'
  | 'boss.minionHp'
  | 'boss.minionSpeed'
  | 'boss.minionCollisionDamage'
  | 'bossLab.hpMultiplier'
  | 'bossLab.speedMultiplier'
  | 'bossLab.damageMultiplier'
  | 'specialDrops.upgradeDropChance'
  | 'specialDrops.treasureChancePerWave'
  | 'specialDrops.treasureMinWave'
  | 'specialDrops.treasureSpawnDelayMs'
  | 'specialDrops.eliteChancePerWave'
  | 'specialDrops.eliteMinWave'
  | 'specialDrops.eliteHpMultiplier'
  | 'specialDrops.eliteSpeedMultiplier'
  | 'specialDrops.eliteMaterialReward'
  | 'specialDrops.healAmount'
  | 'specialDrops.xpAmount'
  | 'specialDrops.hasteDurationMs'
  | 'specialDrops.hasteMoveSpeedBonus';

export interface TunableField {
  path: TunablePath;
  label: string;
  min: number;
  max: number;
  step: number;
}

type TunablePatch = {
  [Group in keyof TunableConfig]?: Partial<TunableConfig[Group]>;
};

export interface TunablePreset {
  id: string;
  label: string;
  description: string;
  patch: TunablePatch;
}

export const TUNABLE_FIELDS: readonly TunableField[] = [
  { path: 'player.speedMultiplier', label: '玩家速度倍率', min: 0.5, max: 2, step: 0.05 },
  { path: 'player.maxHpBonus', label: '玩家生命加成', min: -50, max: 100, step: 5 },
  { path: 'partners.damageMultiplier', label: '伙伴伤害倍率', min: 0.3, max: 3, step: 0.05 },
  { path: 'partners.rangeMultiplier', label: '伙伴射程倍率', min: 0.35, max: 2.5, step: 0.05 },
  { path: 'partners.attackSpeedMultiplier', label: '伙伴攻速倍率', min: 0.4, max: 3, step: 0.05 },
  { path: 'partners.projectileSpeedMultiplier', label: '弹道速度倍率', min: 0.4, max: 2.5, step: 0.05 },
  { path: 'enemies.hpMultiplier', label: '敌人生命倍率', min: 0.4, max: 4, step: 0.05 },
  { path: 'enemies.hpPerWave', label: '每波生命成长', min: 0, max: 0.35, step: 0.01 },
  { path: 'enemies.speedMultiplier', label: '敌人速度倍率', min: 0.4, max: 3, step: 0.05 },
  { path: 'enemies.speedPerWave', label: '每波速度成长', min: 0, max: 0.2, step: 0.01 },
  { path: 'enemies.collisionDamageMultiplier', label: '碰撞伤害倍率', min: 0.4, max: 3, step: 0.05 },
  { path: 'enemies.maxActiveBase', label: '同屏敌人基础上限', min: 0, max: 60, step: 1 },
  { path: 'enemies.maxActivePerWave', label: '每波同屏上限成长', min: 0, max: 5, step: 1 },
  { path: 'enemies.spawnIntervalBaseMs', label: '刷怪基础间隔ms', min: 300, max: 2200, step: 20 },
  { path: 'enemies.spawnIntervalStepMs', label: '每波刷怪间隔减少ms', min: 0, max: 180, step: 5 },
  { path: 'enemies.minSpawnIntervalMs', label: '刷怪最小间隔ms', min: 160, max: 1200, step: 20 },
  { path: 'enemies.spawnIntervalMultiplier', label: '刷怪间隔倍率', min: 0.35, max: 2.5, step: 0.05 },
  { path: 'enemies.specialUnlockTimeMultiplier', label: '特殊怪解锁时间倍率', min: 0.2, max: 1.5, step: 0.05 },
  { path: 'economy.materialValue', label: '材料单颗价值', min: 1, max: 5, step: 1 },
  { path: 'economy.materialDropsPerEnemy', label: '敌人掉落颗数', min: 1, max: 6, step: 0.25 },
  { path: 'economy.pickupRadiusBaseMultiplier', label: '拾取基础半径倍率', min: 0.8, max: 6, step: 0.05 },
  { path: 'economy.shopPriceMultiplier', label: '商店价格倍率', min: 0.4, max: 2.5, step: 0.05 },
  { path: 'economy.rerollCostMultiplier', label: '刷新价格倍率', min: 0.4, max: 3, step: 0.05 },
  { path: 'pricing.partnerPriceMultiplier', label: '伙伴价格倍率', min: 0.3, max: 3, step: 0.05 },
  { path: 'pricing.relicPriceMultiplier', label: '遗物价格倍率', min: 0.3, max: 3, step: 0.05 },
  { path: 'pricing.partnerLevel2PriceMultiplier', label: 'Lv2伙伴价格倍率', min: 1, max: 4, step: 0.05 },
  { path: 'pricing.partnerLevel3PriceMultiplier', label: 'Lv3伙伴价格倍率', min: 1, max: 7, step: 0.05 },
  { path: 'pricing.partnerLevel4PriceMultiplier', label: 'Lv4伙伴价格倍率', min: 1, max: 12, step: 0.1 },
  { path: 'pricing.partnerLevel5PriceMultiplier', label: 'Lv5伙伴价格倍率', min: 1, max: 20, step: 0.25 },
  { path: 'pricing.rerollBaseCost', label: '刷新基础价格', min: 0, max: 20, step: 1 },
  { path: 'pricing.rerollCostPerTwoWaves', label: '每2波刷新涨价', min: 0, max: 8, step: 1 },
  { path: 'pricing.rerollSecondCostMultiplier', label: '连续第2次刷新倍率', min: 1, max: 5, step: 0.05 },
  { path: 'pricing.rerollThirdCostMultiplier', label: '连续第3次刷新倍率', min: 1, max: 6, step: 0.05 },
  { path: 'pricing.rerollMaxCostMultiplier', label: '连续刷新封顶倍率', min: 1, max: 8, step: 0.05 },
  { path: 'pricing.sellRefundRate', label: '出售返还比例', min: 0, max: 1, step: 0.05 },
  { path: 'pricing.firstWaveMinPartnerBudget', label: '第一波伙伴保底材料', min: 0, max: 40, step: 1 },
  { path: 'waves.firstDurationSeconds', label: '第一波时长', min: 8, max: 90, step: 1 },
  { path: 'waves.durationStepSeconds', label: '每波时长成长', min: 0, max: 30, step: 0.5 },
  { path: 'waves.maxDurationSeconds', label: '波次最长时长', min: 20, max: 180, step: 1 },
  { path: 'waves.levelUpHeal', label: '升级回血', min: 0, max: 30, step: 1 },
  { path: 'boss.hp', label: 'Boss生命', min: 50, max: 800, step: 10 },
  { path: 'boss.radius', label: 'Boss半径', min: 18, max: 48, step: 1 },
  { path: 'boss.phaseOneSpeed', label: 'Boss一阶段速度', min: 30, max: 220, step: 5 },
  { path: 'boss.phaseTwoSpeed', label: 'Boss二阶段速度', min: 40, max: 280, step: 5 },
  { path: 'boss.collisionDamage', label: 'Boss碰撞伤害', min: 5, max: 60, step: 1 },
  { path: 'boss.shockwaveDamage', label: 'Boss冲击波伤害', min: 4, max: 60, step: 1 },
  { path: 'boss.minionHp', label: 'Boss小怪生命', min: 1, max: 40, step: 1 },
  { path: 'boss.minionSpeed', label: 'Boss小怪速度', min: 30, max: 240, step: 5 },
  { path: 'boss.minionCollisionDamage', label: 'Boss小怪碰撞伤害', min: 2, max: 40, step: 1 },
  { path: 'bossLab.hpMultiplier', label: '实验室Boss生命倍率', min: 0.1, max: 5, step: 0.1 },
  { path: 'bossLab.speedMultiplier', label: '实验室Boss速度倍率', min: 0.1, max: 3, step: 0.1 },
  { path: 'bossLab.damageMultiplier', label: '实验室Boss伤害倍率', min: 0.1, max: 5, step: 0.1 },
  { path: 'specialDrops.upgradeDropChance', label: '升级奖励掉落率', min: 0, max: 1, step: 0.05 },
  { path: 'specialDrops.treasureChancePerWave', label: '宝箱每波概率', min: 0, max: 1, step: 0.05 },
  { path: 'specialDrops.treasureMinWave', label: '宝箱起始波', min: 1, max: 10, step: 1 },
  { path: 'specialDrops.treasureSpawnDelayMs', label: '宝箱延迟ms', min: 0, max: 30000, step: 500 },
  { path: 'specialDrops.eliteChancePerWave', label: '精英怪每波概率', min: 0, max: 1, step: 0.05 },
  { path: 'specialDrops.eliteMinWave', label: '精英怪起始波', min: 1, max: 10, step: 1 },
  { path: 'specialDrops.eliteHpMultiplier', label: '精英怪生命倍率', min: 1, max: 8, step: 0.25 },
  { path: 'specialDrops.eliteSpeedMultiplier', label: '精英怪速度倍率', min: 0.3, max: 2, step: 0.05 },
  { path: 'specialDrops.eliteMaterialReward', label: '精英材料包', min: 1, max: 30, step: 1 },
  { path: 'specialDrops.healAmount', label: '回血球数值', min: 1, max: 40, step: 1 },
  { path: 'specialDrops.xpAmount', label: '经验球数值', min: 1, max: 30, step: 1 },
  { path: 'specialDrops.hasteDurationMs', label: '加速球持续ms', min: 1000, max: 15000, step: 500 },
  { path: 'specialDrops.hasteMoveSpeedBonus', label: '加速球移速加成', min: 0.05, max: 1, step: 0.05 },
];

export const TUNABLE_PRESETS: readonly TunablePreset[] = [
  {
    id: 'difficulty-1',
    label: '1',
    description: '轻压力体验构筑，Boss 伤害和血量降低。',
    patch: {
      partners: { damageMultiplier: 1.1, rangeMultiplier: 1.12, attackSpeedMultiplier: 1.08 },
      enemies: {
        hpMultiplier: 0.75,
        hpPerWave: 0.08,
        speedMultiplier: 0.85,
        speedPerWave: 0.04,
        collisionDamageMultiplier: 0.75,
        maxActiveBase: 10,
        maxActivePerWave: 1,
        spawnIntervalMultiplier: 1.25,
      },
      economy: { materialValue: 2, shopPriceMultiplier: 0.85, rerollCostMultiplier: 0.8 },
      boss: {
        hp: 150,
        phaseOneSpeed: 82,
        phaseTwoSpeed: 120,
        collisionDamage: 14,
        shockwaveDamage: 10,
        minionHp: 4,
        minionSpeed: 88,
        minionCollisionDamage: 5,
      },
      specialDrops: { upgradeDropChance: 0.45, eliteChancePerWave: 0.25, treasureChancePerWave: 0.14 },
    },
  },
  {
    id: 'difficulty-2',
    label: '2',
    description: '轻松但需要构筑，以真实自动玩家能稳定过前中期为准。',
    patch: {
      partners: { damageMultiplier: 1.05, rangeMultiplier: 1.05, attackSpeedMultiplier: 1.04 },
      enemies: {
        hpMultiplier: 1,
        hpPerWave: 0.1,
        speedMultiplier: 0.9,
        speedPerWave: 0.05,
        collisionDamageMultiplier: 0.9,
        maxActiveBase: 13,
        maxActivePerWave: 1,
        spawnIntervalMultiplier: 1,
      },
      economy: { materialValue: 2, materialDropsPerEnemy: 1, shopPriceMultiplier: 1.05, rerollCostMultiplier: 1 },
      boss: {
        hp: 220,
        phaseOneSpeed: 94,
        phaseTwoSpeed: 138,
        collisionDamage: 20,
        shockwaveDamage: 14,
        minionHp: 7,
        minionSpeed: 100,
        minionCollisionDamage: 8,
      },
      specialDrops: { upgradeDropChance: 0.36, eliteChancePerWave: 0.35, treasureChancePerWave: 0.1 },
      pricing: {
        partnerPriceMultiplier: 1.05,
        relicPriceMultiplier: 1,
        partnerLevel2PriceMultiplier: 1.95,
        partnerLevel3PriceMultiplier: 3.6,
        partnerLevel4PriceMultiplier: 6.9,
        partnerLevel5PriceMultiplier: 12.8,
      },
    },
  },
  {
    id: 'difficulty-3',
    label: '3',
    description: '标准难度。真实走位下要求稳定成型路线。',
    patch: {
      boss: {
        hp: 300,
        phaseOneSpeed: 108,
        phaseTwoSpeed: 160,
        collisionDamage: 24,
        shockwaveDamage: 18,
        minionHp: 9,
        minionCollisionDamage: 10,
      },
      enemies: {
        hpMultiplier: 1.15,
        hpPerWave: 0.14,
        speedMultiplier: 1,
        speedPerWave: 0.07,
        maxActiveBase: 15,
        maxActivePerWave: 2,
        spawnIntervalMultiplier: 0.92,
        collisionDamageMultiplier: 1.08,
      },
      economy: {
        materialValue: 1,
        materialDropsPerEnemy: 1.5,
        shopPriceMultiplier: 1.08,
        rerollCostMultiplier: 1.08,
      },
      pricing: {
        partnerPriceMultiplier: 1.08,
        relicPriceMultiplier: 1,
        partnerLevel2PriceMultiplier: 2.1,
        partnerLevel3PriceMultiplier: 3.9,
        partnerLevel4PriceMultiplier: 7.4,
        partnerLevel5PriceMultiplier: 13.6,
      },
    },
  },
  {
    id: 'difficulty-4',
    label: '4',
    description: '敌人更硬更快，Boss 明显更有压迫。',
    patch: {
      enemies: {
        hpMultiplier: 1.45,
        hpPerWave: 0.18,
        speedMultiplier: 1.12,
        speedPerWave: 0.09,
        collisionDamageMultiplier: 1.28,
        maxActiveBase: 18,
        maxActivePerWave: 3,
        spawnIntervalMultiplier: 0.78,
      },
      economy: { materialValue: 1, materialDropsPerEnemy: 1.25, shopPriceMultiplier: 1.2, rerollCostMultiplier: 1.18 },
      boss: {
        hp: 390,
        phaseOneSpeed: 128,
        phaseTwoSpeed: 195,
        collisionDamage: 30,
        shockwaveDamage: 24,
        minionHp: 12,
        minionSpeed: 136,
        minionCollisionDamage: 13,
      },
      specialDrops: { eliteChancePerWave: 0.65, treasureChancePerWave: 0.06 },
      pricing: {
        partnerPriceMultiplier: 1.16,
        relicPriceMultiplier: 0.88,
        partnerLevel2PriceMultiplier: 2.35,
        partnerLevel3PriceMultiplier: 4.4,
        partnerLevel4PriceMultiplier: 8.4,
        partnerLevel5PriceMultiplier: 15.5,
      },
    },
  },
  {
    id: 'difficulty-5',
    label: '5',
    description: '高压测试。精英必出，Boss 血量和技能伤害大幅提高。',
    patch: {
      player: { maxHpBonus: -15 },
      partners: { rangeMultiplier: 0.9 },
      enemies: {
        hpMultiplier: 1.8,
        hpPerWave: 0.23,
        speedMultiplier: 1.25,
        speedPerWave: 0.12,
        collisionDamageMultiplier: 1.45,
        maxActiveBase: 21,
        maxActivePerWave: 4,
        spawnIntervalMultiplier: 0.68,
        minSpawnIntervalMs: 220,
        specialUnlockTimeMultiplier: 0.35,
      },
      economy: { materialValue: 1, materialDropsPerEnemy: 1.1, shopPriceMultiplier: 1.35, rerollCostMultiplier: 1.4 },
      boss: {
        hp: 520,
        phaseOneSpeed: 155,
        phaseTwoSpeed: 235,
        collisionDamage: 36,
        shockwaveDamage: 30,
        minionHp: 15,
        minionSpeed: 168,
        minionCollisionDamage: 16,
      },
      specialDrops: { eliteChancePerWave: 1, eliteHpMultiplier: 4.5, eliteSpeedMultiplier: 0.95, treasureChancePerWave: 0.04 },
      pricing: {
        partnerPriceMultiplier: 1.25,
        relicPriceMultiplier: 0.82,
        partnerLevel2PriceMultiplier: 2.5,
        partnerLevel3PriceMultiplier: 4.8,
        partnerLevel4PriceMultiplier: 9.2,
        partnerLevel5PriceMultiplier: 17,
      },
    },
  },
  {
    id: 'boss-practice',
    label: 'Boss',
    description: '关闭普通刷怪，给足经济，用 Boss 实验室测手感。',
    patch: {
      player: { maxHpBonus: 80 },
      partners: { damageMultiplier: 1.25, rangeMultiplier: 1.25, attackSpeedMultiplier: 1.2 },
      enemies: {
        maxActiveBase: 0,
        maxActivePerWave: 0,
        spawnIntervalBaseMs: 2200,
        spawnIntervalStepMs: 0,
        spawnIntervalMultiplier: 2.5,
      },
      economy: { materialValue: 5, materialDropsPerEnemy: 5, shopPriceMultiplier: 0.4, rerollCostMultiplier: 0.4 },
      boss: { hp: 260, collisionDamage: 16, shockwaveDamage: 12, minionHp: 4, minionCollisionDamage: 4 },
      bossLab: { hpMultiplier: 1, speedMultiplier: 1, damageMultiplier: 1 },
      specialDrops: { eliteChancePerWave: 0, treasureChancePerWave: 0.25 },
    },
  },
  {
    id: 'scan-conservative',
    label: '扫描-保守',
    description: '保留默认难度，用仿真修正后的保守压力组合测试手感。',
    patch: {
      enemies: {
        hpMultiplier: 1.5,
        maxActiveBase: 17,
        maxActivePerWave: 3,
        spawnIntervalMultiplier: 0.83,
        collisionDamageMultiplier: 1.25,
      },
    },
  },
  {
    id: 'scan-standard',
    label: '扫描-标准',
    description: '保留默认难度，用仿真原始推荐的标准压力组合测试手感。',
    patch: {
      enemies: {
        hpMultiplier: 2.25,
        maxActiveBase: 22,
        maxActivePerWave: 3,
        spawnIntervalMultiplier: 0.625,
        collisionDamageMultiplier: 2,
      },
    },
  },
  {
    id: 'scan-hard',
    label: '扫描-困难',
    description: '保留默认难度，用仿真推荐的困难压力组合测试手感。',
    patch: {
      enemies: {
        hpMultiplier: 2.5,
        maxActiveBase: 14,
        maxActivePerWave: 2,
        spawnIntervalMultiplier: 1,
        collisionDamageMultiplier: 1.75,
      },
    },
  },
  {
    id: 'build-test',
    label: '构筑测试',
    description: '高经济和高掉落，快速验证遗物、升星和升级组合。',
    patch: {
      player: { maxHpBonus: 40 },
      partners: { damageMultiplier: 1.2, attackSpeedMultiplier: 1.15 },
      enemies: {
        hpMultiplier: 1.1,
        hpPerWave: 0.12,
        speedMultiplier: 0.95,
        maxActiveBase: 12,
        maxActivePerWave: 1,
        spawnIntervalMultiplier: 1.15,
      },
      economy: { materialValue: 4, materialDropsPerEnemy: 3, shopPriceMultiplier: 0.55, rerollCostMultiplier: 0.5 },
      specialDrops: {
        upgradeDropChance: 0.75,
        treasureChancePerWave: 0.35,
        eliteChancePerWave: 0.85,
        eliteMaterialReward: 18,
      },
    },
  },
];

const STORAGE_KEY = 'anime-brotato:tunables:v4';
const runtimeTunables = clone(DEFAULT_TUNABLES);

loadTunablesFromStorage();
exposeTunablesForConsole();

export function getTunables() {
  return runtimeTunables;
}

export function setTunableValue(path: TunablePath, value: number) {
  const field = TUNABLE_FIELDS.find((item) => item.path === path);
  if (!field) {
    throw new Error(`Unknown tunable path: ${path}`);
  }
  if (!Number.isFinite(value)) {
    throw new Error(`Invalid tunable value for ${path}: ${value}`);
  }
  const clamped = clampToField(value, field);
  setByPath(runtimeTunables, path, clamped);
  saveTunables();
}

export function resetTunables() {
  replaceTunables(clone(DEFAULT_TUNABLES));
  window.localStorage.removeItem(STORAGE_KEY);
}

export function applyTunablePreset(id: string) {
  if (id === 'random') {
    applyTunablePatch(createRandomTunablePatch());
    return;
  }

  const preset = TUNABLE_PRESETS.find((item) => item.id === id);
  if (!preset) {
    throw new Error(`Unknown tunable preset: ${id}`);
  }
  applyTunablePatch(preset.patch);
}

export function applyTunablePatch(patch: TunablePatch) {
  const next = mergeTunables(clone(DEFAULT_TUNABLES), patch);
  validateTunables(next);
  replaceTunables(next);
  saveTunables();
}

export function exportTunables() {
  return JSON.stringify(runtimeTunables, null, 2);
}

export function importTunables(rawJson: string) {
  const parsed = JSON.parse(rawJson) as unknown;
  const next = mergeTunables(clone(DEFAULT_TUNABLES), parsed);
  validateTunables(next);
  replaceTunables(next);
  saveTunables();
}

export function getTunableValue(path: TunablePath) {
  const [group, key] = path.split('.') as [keyof TunableConfig, string];
  const groupValue = runtimeTunables[group] as Record<string, number>;
  const value = groupValue[key];
  if (!Number.isFinite(value)) {
    throw new Error(`Invalid tunable value at ${path}: ${value}`);
  }
  return value;
}

function loadTunablesFromStorage() {
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (!stored) {
    return;
  }
  importTunables(stored);
}

function saveTunables() {
  window.localStorage.setItem(STORAGE_KEY, exportTunables());
}

function mergeTunables(base: TunableConfig, input: unknown): TunableConfig {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    throw new Error('Tunables import must be a JSON object');
  }

  const source = input as Record<string, unknown>;
  for (const field of TUNABLE_FIELDS) {
    const [group, key] = field.path.split('.') as [keyof TunableConfig, string];
    const sourceGroup = source[group];
    if (typeof sourceGroup !== 'object' || sourceGroup === null || Array.isArray(sourceGroup)) {
      continue;
    }
    const value = (sourceGroup as Record<string, unknown>)[key];
    if (value === undefined) {
      continue;
    }
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      throw new Error(`Invalid tunable value for ${field.path}: ${value}`);
    }
    setByPath(base, field.path, clampToField(value, field));
  }
  return base;
}

function validateTunables(config: TunableConfig) {
  for (const field of TUNABLE_FIELDS) {
    const value = getValueFrom(config, field.path);
    if (!Number.isFinite(value) || value < field.min || value > field.max) {
      throw new Error(`Invalid tunable value for ${field.path}: ${value}`);
    }
  }
}

function replaceTunables(next: TunableConfig) {
  for (const group of Object.keys(DEFAULT_TUNABLES) as Array<keyof TunableConfig>) {
    Object.assign(runtimeTunables[group], next[group]);
  }
}

function getValueFrom(config: TunableConfig, path: TunablePath) {
  const [group, key] = path.split('.') as [keyof TunableConfig, string];
  return (config[group] as Record<string, number>)[key];
}

function setByPath(config: TunableConfig, path: TunablePath, value: number) {
  const [group, key] = path.split('.') as [keyof TunableConfig, string];
  (config[group] as Record<string, number>)[key] = value;
}

function createRandomTunablePatch(): TunablePatch {
  const patch = clone(DEFAULT_TUNABLES) as TunablePatch;
  for (const field of TUNABLE_FIELDS) {
    const baseline = getValueFrom(DEFAULT_TUNABLES, field.path);
    const spread = baseline === 0 ? Math.max(field.step, (field.max - field.min) * 0.08) : Math.abs(baseline) * 0.3;
    const randomFactor = Math.random() * 2 - 1;
    setPatchValue(patch, field.path, clampToField(baseline + spread * randomFactor, field));
  }
  return patch;
}

function setPatchValue(config: TunablePatch, path: TunablePath, value: number) {
  const [group, key] = path.split('.') as [keyof TunableConfig, string];
  const groupValue = (config[group] ?? {}) as Record<string, number>;
  groupValue[key] = value;
  config[group] = groupValue as TunablePatch[typeof group];
}

function clampToField(value: number, field: TunableField) {
  return Math.min(field.max, Math.max(field.min, value));
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function exposeTunablesForConsole() {
  Object.assign(window, {
    animeBrotatoTunables: {
      get: getTunables,
      set: setTunableValue,
      reset: resetTunables,
      export: exportTunables,
      import: importTunables,
      preset: applyTunablePreset,
    },
  });
}
